import React, { useEffect, useState } from 'react';
import type { User } from '@supabase/supabase-js';
import { Check, X as XIcon, Loader2, Crown, Sparkles, ExternalLink } from 'lucide-react';
import {
  DOORSTEPS_TIERS,
  fetchSubscription,
  createCheckoutSession,
  openBillingPortal,
  type DoorstepsTier,
  type DoorstepsBillingCycle,
} from '../services/pricing';
import { useToast } from './Toast';

interface PricingScreenProps {
  user: User;
}

export default function PricingScreen({ user }: PricingScreenProps) {
  const [cycle, setCycle] = useState<DoorstepsBillingCycle>('monthly');
  const [currentTier, setCurrentTier] = useState<DoorstepsTier>('free');
  const [currentCycle, setCurrentCycle] = useState<DoorstepsBillingCycle | null>(null);
  const [busyTier, setBusyTier] = useState<DoorstepsTier | null>(null);
  const [loadingPortal, setLoadingPortal] = useState(false);
  const toast = useToast();

  useEffect(() => {
    fetchSubscription(user.id).then((s) => {
      setCurrentTier(s.tier);
      setCurrentCycle(s.billing_cycle);
    });
  }, [user.id]);

  const handleSubscribe = async (tier: Exclude<DoorstepsTier, 'free'>) => {
    setBusyTier(tier);
    try {
      const url = await createCheckoutSession(tier, cycle);
      window.location.href = url;
    } catch (err: any) {
      toast.error(err?.message || 'Could not start checkout');
      setBusyTier(null);
    }
  };

  const handleManage = async () => {
    setLoadingPortal(true);
    try {
      const url = await openBillingPortal();
      window.location.href = url;
    } catch (err: any) {
      toast.error(err?.message || 'Could not open billing portal');
      setLoadingPortal(false);
    }
  };

  const tiers = DOORSTEPS_TIERS;

  return (
    <div className="flex-1 min-h-0 overflow-y-auto bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-semibold mb-3">
            <Sparkles size={12} /> Doorsteps pricing
          </div>
          <h1 className="text-3xl font-semibold text-slate-900">
            Pick the plan that matches your prospecting volume.
          </h1>
          <p className="text-sm text-slate-500 mt-2 max-w-2xl mx-auto">
            Platform subscription + pass-through mail with margin. No bundled letter allowances, no surprise overage charges. 30-day rolling, cancel anytime.
          </p>

          {/* Cycle toggle */}
          <div className="inline-flex items-center gap-1 mt-6 bg-white border border-slate-200 rounded-xl p-1">
            <button
              onClick={() => setCycle('monthly')}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                cycle === 'monthly' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setCycle('annual')}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                cycle === 'annual' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Annual <span className="text-emerald-500 ml-1">2 months free</span>
            </button>
          </div>
        </div>

        {currentTier !== 'free' && (
          <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-emerald-900">
              <Crown size={18} className="text-emerald-600" />
              <span>
                You're on <strong>Doorsteps {DOORSTEPS_TIERS.find((t) => t.tier === currentTier)?.name}</strong>
                {currentCycle && ` · ${currentCycle}`}
              </span>
            </div>
            <button
              onClick={handleManage}
              disabled={loadingPortal}
              className="text-sm font-semibold text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"
            >
              {loadingPortal ? <Loader2 size={14} className="animate-spin" /> : <ExternalLink size={14} />}
              Manage subscription
            </button>
          </div>
        )}

        {/* Tier grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {tiers.map((t) => {
            const price = cycle === 'annual' ? t.annualPriceGBP : t.monthlyPriceGBP;
            const monthlyEquivalent = cycle === 'annual' ? Math.round(t.annualPriceGBP / 12) : t.monthlyPriceGBP;
            const isCurrent = t.tier === currentTier;
            const highlighted = t.tier === 'pro';

            return (
              <div
                key={t.tier}
                className={`relative bg-white border rounded-2xl p-5 flex flex-col ${
                  highlighted ? 'border-emerald-400 ring-1 ring-emerald-200 shadow-lg' : 'border-slate-200'
                }`}
              >
                {highlighted && (
                  <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-emerald-500 text-white text-[10px] font-bold rounded-full uppercase tracking-wide">
                    Most popular
                  </div>
                )}

                <div className="mb-3">
                  <h3 className="text-lg font-semibold text-slate-900">{t.name}</h3>
                  <p className="text-xs text-slate-500 leading-snug mt-0.5">{t.blurb}</p>
                </div>

                <div className="mb-4">
                  {t.tier === 'free' ? (
                    <>
                      <div className="text-3xl font-semibold text-slate-900">£0</div>
                      <div className="text-xs text-slate-500">forever</div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-semibold text-slate-900">£{monthlyEquivalent}</span>
                        <span className="text-xs text-slate-500">/mo</span>
                      </div>
                      <div className="text-xs text-slate-500">
                        {cycle === 'annual' ? `£${price} billed annually` : `Billed monthly`}
                      </div>
                      <div className="mt-2 inline-flex items-center gap-1 text-xs text-slate-700 bg-slate-100 px-2 py-0.5 rounded">
                        <strong>£{(t.perLetterPence / 100).toFixed(2)}</strong>/letter
                      </div>
                    </>
                  )}
                </div>

                <ul className="space-y-1.5 text-sm text-slate-700 flex-1 mb-4">
                  {t.highlights.map((h, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check size={14} className="text-emerald-500 shrink-0 mt-0.5" />
                      <span className="leading-snug">{h}</span>
                    </li>
                  ))}
                </ul>

                {t.tier === 'free' ? (
                  <button
                    disabled
                    className="w-full py-2 bg-slate-100 text-slate-500 rounded-lg text-sm font-semibold cursor-default"
                  >
                    {currentTier === 'free' ? 'Current plan' : 'Default'}
                  </button>
                ) : isCurrent ? (
                  <button
                    onClick={handleManage}
                    disabled={loadingPortal}
                    className="w-full py-2 border border-emerald-300 text-emerald-700 rounded-lg text-sm font-semibold hover:bg-emerald-50"
                  >
                    {loadingPortal ? <Loader2 size={14} className="animate-spin inline" /> : 'Manage subscription'}
                  </button>
                ) : (
                  <button
                    onClick={() => handleSubscribe(t.tier as Exclude<DoorstepsTier, 'free'>)}
                    disabled={busyTier === t.tier}
                    className={`w-full py-2 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 ${
                      highlighted
                        ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
                        : 'bg-slate-900 hover:bg-slate-800 text-white'
                    }`}
                  >
                    {busyTier === t.tier ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : currentTier === 'free' ? (
                      'Subscribe'
                    ) : (
                      'Switch to ' + t.name
                    )}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Capability matrix */}
        <div className="mt-12 bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-200">
            <h2 className="font-semibold text-slate-900">Plan comparison</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2 font-semibold">Feature</th>
                  {tiers.map((t) => (
                    <th key={t.tier} className="text-center px-4 py-2 font-semibold">{t.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                <Row label="Saved territories" cells={tiers.map((t) => String(t.limits.savedTerritories))} />
                <Row label="Active branches" cells={tiers.map((t) => String(t.limits.activeBranches))} />
                <Row label="Seats included" cells={tiers.map((t) => String(t.limits.seats))} />
                <Row label="Send campaigns" cells={tiers.map((t) => t.limits.canSendCampaigns)} />
                <Row label="Lead pipeline / kanban" cells={tiers.map((t) => t.limits.canAccessPipeline)} />
                <Row label="Multi-template campaigns" cells={tiers.map((t) => t.limits.multiTemplate)} />
                <Row label="A/B testing" cells={tiers.map((t) => t.limits.abTesting)} />
                <Row label="Per-branch lead routing" cells={tiers.map((t) => t.limits.perBranchLeadRouting)} />
                <Row label="Custom subdomain" cells={tiers.map((t) => t.limits.customSubdomain)} />
                <Row label="API access" cells={tiers.map((t) => t.limits.apiAccess)} />
                <Row label="Priority support" cells={tiers.map((t) => t.limits.prioritySupport)} />
              </tbody>
            </table>
          </div>
        </div>

        <p className="text-xs text-slate-400 text-center mt-6">
          Mail rate is what you pay per letter posted on top of the platform subscription. Stannp's underlying cost is ~£0.98/letter at A5 enveloped second-class.
        </p>
      </div>
    </div>
  );
}

function Row({ label, cells }: { label: string; cells: (string | boolean)[] }) {
  return (
    <tr>
      <td className="px-4 py-2 text-slate-700">{label}</td>
      {cells.map((c, i) => (
        <td key={i} className="px-4 py-2 text-center">
          {typeof c === 'boolean' ? (
            c ? <Check size={14} className="text-emerald-500 inline" /> : <XIcon size={14} className="text-slate-300 inline" />
          ) : (
            <span className="text-sm text-slate-700">{c}</span>
          )}
        </td>
      ))}
    </tr>
  );
}
